/**
 * One public content identity for BarbershopTalk.
 * The Capacitor WebView origin is never a shareable or routable public URL.
 */

export const PUBLIC_ORIGIN = 'https://www.barbershoptalk.com';
export const APP_ASSOCIATION_ID = '7JRUCR9XKC.com.barbershoptalk.app';

const ALLOWED_HOSTS = new Set([
  'www.barbershoptalk.com',
  'barbershoptalk.com',
]);

const PRIVATE_PREFIXES = [
  '/login',
  '/register',
  '/logout',
  '/settings',
  '/notifications',
  '/notification-preferences',
  '/forgot-password',
  '/new-password',
  '/sports/favorites',
  '/subscription',
  '/admin',
  '/searchArticles',
  '/images',
  '/thefeed',
];

/** Dynamic public trees. Subpaths stay inside the app. */
const PUBLIC_TREES = [
  '/the-barbershop',
  '/politics',
  '/profiles',
  '/sports/nba',
  '/sports/nhl',
  '/nfl',
  '/ufc',
  '/business',
  '/technology',
  '/movies',
  '/tvNews',
  '/musicNews',
  '/inTheaters',
];

/** Exact public pages that should not claim an open subtree by themselves. */
const PUBLIC_EXACT = [
  '/',
  '/trending',
  '/business',
  '/technology',
  '/the-barbershop',
  '/politics',
  '/politics/intelligence',
  '/politics/right-wing',
  '/politics/left-wing',
  '/politics/center',
  '/movies',
  '/tvNews',
  '/musicNews',
  '/inTheaters',
  '/sports/nba',
  '/sports/nfl',
  '/sports/nhl',
  '/sports/mma',
  '/nfl',
  '/nfl/games',
  '/nfl/standings',
  '/nfl/power-rankings',
  '/ufc',
  '/ufc/events',
  '/ufc/rankings',
  '/newsletter',
  '/pricing',
  '/about',
  '/methodology',
  '/under-sniper',
  '/picks',
  '/editorial-policy',
  '/privacy',
  '/terms',
  '/community-guidelines',
  '/how-sentiment-works',
  '/how-bias-analysis-works',
  '/how-blindspots-work',
  '/challenge-your-view',
];

const PUBLIC_TREE_DEPTH = 3;
const PRIVATE_TREE_DEPTH = 6;

function isPrivatePath(pathname) {
  if (pathname === '/profile' || pathname.startsWith('/profile/')) return true;
  return PRIVATE_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

export function normalizePathname(pathname) {
  if (pathname == null) return null;
  let path = String(pathname);
  if (!path || path.includes('\\') || path.includes('\0')) return null;
  try {
    path = decodeURI(path);
  } catch {
    return null;
  }
  if (!path.startsWith('/')) path = `/${path}`;
  if (path.startsWith('//')) return null;
  const segments = path.split('/');
  if (segments.some((segment) => segment === '.' || segment === '..')) return null;
  if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);
  return path || '/';
}

function pathnameSegments(pathname) {
  return pathname.split('/').filter(Boolean);
}

function isPublicArticlePath(pathname) {
  const parts = pathnameSegments(pathname);
  return parts.length === 3 && /^\d+$/.test(parts[2]);
}

function isLegacyBoxScorePath(pathname) {
  const parts = pathnameSegments(pathname);
  return parts.length === 5 && parts[3] === 'boxscore' && /^\d+$/.test(parts[4]);
}

function matchesPublicTree(pathname) {
  return PUBLIC_TREES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

/**
 * True when this pathname is safe to open from an external Universal Link.
 * Private account, admin, search, and auth routes are rejected.
 */
export function isAllowedPublicDeepLink(pathname) {
  const path = normalizePathname(pathname);
  if (!path) return false;
  if (isPrivatePath(path)) return false;
  if (PUBLIC_EXACT.includes(path) || matchesPublicTree(path)) return true;
  if (isPublicArticlePath(path) || isLegacyBoxScorePath(path)) return true;
  return false;
}

function rejectedScheme(raw) {
  const lower = String(raw).trim().toLowerCase();
  return (
    lower.startsWith('javascript:')
    || lower.startsWith('file:')
    || lower.startsWith('capacitor:')
    || lower.startsWith('data:')
    || lower.startsWith('blob:')
    || lower.startsWith('about:')
  );
}

/**
 * Build the canonical HTTPS URL for an in-app path or an already-public URL.
 * Returns null for WebView, localhost, API, and foreign origins.
 */
export function buildPublicUrl(pathOrFullPath) {
  if (pathOrFullPath == null) return null;
  const raw = String(pathOrFullPath).trim();
  if (!raw || rejectedScheme(raw) || raw.includes('\\')) return null;

  let url;
  try {
    if (/^https:\/\//i.test(raw)) {
      url = new URL(raw);
      const host = url.hostname.toLowerCase();
      if (!ALLOWED_HOSTS.has(host)) return null;
    } else if (raw.startsWith('/')) {
      if (raw.startsWith('//')) return null;
      url = new URL(raw, PUBLIC_ORIGIN);
    } else {
      return null;
    }
  } catch {
    return null;
  }

  if (url.username || url.password) return null;
  const pathname = normalizePathname(url.pathname);
  if (!pathname) return null;
  const canonical = new URL(PUBLIC_ORIGIN);
  canonical.pathname = pathname;
  canonical.search = url.search;
  canonical.hash = url.hash;
  return canonical.toString();
}

/**
 * Parse an inbound location into a Vue route location.
 * Accepts public HTTPS URLs and in-app paths. Does not apply the public-route allowlist,
 * so notification clicks can still target private pages and hit the auth guard.
 * Returns null for rejected schemes and foreign hosts.
 */
export function parseInboundLocation(urlOrPath) {
  if (urlOrPath == null) return null;
  const raw = String(urlOrPath).trim();
  if (!raw || rejectedScheme(raw)) return null;

  let url;
  try {
    if (raw.startsWith('/')) {
      if (raw.startsWith('//')) return null;
      url = new URL(raw, PUBLIC_ORIGIN);
    } else if (/^https:\/\//i.test(raw)) {
      url = new URL(raw);
      if (!ALLOWED_HOSTS.has(url.hostname.toLowerCase())) return null;
    } else {
      return null;
    }
  } catch {
    return null;
  }

  if (url.username || url.password) return null;
  const pathname = normalizePathname(url.pathname);
  if (!pathname) return null;
  const search = url.search || '';
  const hash = url.hash || '';
  return {
    pathname,
    search,
    hash,
    fullPath: `${pathname}${search}${hash}`,
  };
}

/**
 * Universal Link target. Foreign, internal, and private paths return null.
 */
export function routeTargetForIncomingUrl(urlOrPath) {
  const parsed = parseInboundLocation(urlOrPath);
  if (!parsed) return null;
  if (!isAllowedPublicDeepLink(parsed.pathname)) return null;
  return parsed.fullPath;
}

export function shareIntentHrefs({ url, title, description, summary }) {
  const publicUrl = buildPublicUrl(url);
  if (!publicUrl) return null;
  const safeTitle = title == null ? '' : String(title);
  const safeDescription = description == null ? '' : String(description);
  const safeSummary = summary == null ? '' : String(summary);
  return {
    url: publicUrl,
    twitter: `https://twitter.com/share?url=${encodeURIComponent(publicUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(publicUrl)}&title=${encodeURIComponent(safeTitle)}&description=${encodeURIComponent(safeDescription)}`,
    reddit: `https://www.reddit.com/submit?url=${encodeURIComponent(publicUrl)}&title=${encodeURIComponent(safeTitle)}`,
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(publicUrl)}&title=${encodeURIComponent(safeTitle)}&summary=${encodeURIComponent(safeSummary)}`,
    telegram: `https://t.me/share/url?url=${encodeURIComponent(publicUrl)}&text=${encodeURIComponent(safeTitle)}`,
    sms: `sms:?body=${encodeURIComponent(`${safeTitle} - ${publicUrl}`)}`,
    email: `mailto:?subject=${encodeURIComponent(safeTitle)}&body=${encodeURIComponent(`${safeSummary} - ${publicUrl}`)}`,
  };
}

/**
 * Smart App Banner content attribute. Returns null unless explicitly enabled
 * with a numeric App Store ID and a canonical www URL. Default callers pass enabled: false.
 */
export function appleItunesAppContent({ enabled, appStoreId, canonicalUrl }) {
  if (enabled !== true) return null;
  const id = String(appStoreId || '');
  if (!/^\d+$/.test(id)) return null;
  let canonical;
  try {
    canonical = new URL(canonicalUrl);
  } catch {
    return null;
  }
  if (canonical.protocol !== 'https:' || canonical.hostname !== 'www.barbershoptalk.com') return null;
  return `app-id=${id}, app-argument=${canonical.toString()}`;
}

function patternVariants(prefix, depth) {
  const variants = [prefix];
  let current = prefix;
  for (let i = 0; i < depth; i += 1) {
    current += '/*';
    variants.push(current);
  }
  return variants;
}

function component(path, extra) {
  return { '/': path, ...extra };
}

/**
 * `*` matches exactly one path segment. Patterns are evaluated in order.
 * The first match wins, which is how iOS applies AASA components.
 */
export function aasaPatternMatches(pattern, pathname) {
  const path = normalizePathname(pathname);
  if (!path || !pattern) return false;
  const patternSegments = pattern.split('/').slice(1);
  const pathSegments = path.split('/').slice(1);
  if (patternSegments.length !== pathSegments.length) return false;
  for (let i = 0; i < patternSegments.length; i += 1) {
    if (patternSegments[i] === '*') continue;
    if (patternSegments[i] !== pathSegments[i]) return false;
  }
  return true;
}

export function associationOpensPath(pathname) {
  const components = buildAppleAppSiteAssociation().applinks.details[0].components;
  for (const entry of components) {
    if (aasaPatternMatches(entry['/'], pathname)) {
      return entry.exclude !== true;
    }
  }
  return false;
}

export function buildAppleAppSiteAssociation() {
  const components = [];

  PRIVATE_PREFIXES.forEach((prefix) => {
    patternVariants(prefix, PRIVATE_TREE_DEPTH).forEach((pattern) => {
      components.push(component(pattern, {
        exclude: true,
        comment: `Private ${prefix}`,
      }));
    });
  });
  patternVariants('/profile', PRIVATE_TREE_DEPTH).forEach((pattern) => {
    components.push(component(pattern, {
      exclude: true,
      comment: 'Private account profile. Public profiles live at /profiles.',
    }));
  });

  PUBLIC_EXACT.forEach((path) => {
    if (path === '/') {
      components.push(component('/', { comment: 'Home' }));
      return;
    }
    components.push(component(path, { comment: `Public ${path}` }));
  });

  PUBLIC_TREES.forEach((prefix) => {
    patternVariants(prefix, PUBLIC_TREE_DEPTH).forEach((pattern) => {
      if (pattern === prefix) return;
      components.push(component(pattern, { comment: `Public ${prefix}` }));
    });
  });

  components.push(component('/*/*/*', {
    comment: 'Public article URLs /:category/:slug/:id. Private prefixes are excluded above.',
  }));
  components.push(component('/*/*/*/boxscore/*', {
    comment: 'Legacy public box score URLs.',
  }));

  return {
    applinks: {
      apps: [],
      details: [
        {
          appIDs: [APP_ASSOCIATION_ID],
          components,
        },
      ],
    },
  };
}
